import React, { useEffect, useState } from 'react';
import ShadcnScope from 'App/shadcn/components/ShadcnScope';
import EventBus from 'App/services/EventBus';
import AgentApprovalsResource, { type AgentApproval } from 'Resources/AgentApprovals';
import { AIGeneratedContentCard } from 'Components/AIGeneratedContentCard/AIGeneratedContentCard';
import T from 'Components/Helpers/translate';
import WriteCard from 'App/shadcn/pages/Assistant/WriteCard';
import MultiActionCard from 'App/shadcn/pages/Assistant/MultiActionWriteCard';
import EmailDraftCard from 'App/shadcn/pages/Assistant/EmailDraftCard';
import OfferDraftCard from 'App/shadcn/pages/Assistant/OfferDraftCard';
import {
	approvalToWriteProposal,
	approvalToMultiActionConfig,
	approvalToEmailDraft,
	approvalToOfferDraft
} from 'App/shadcn/pages/Assistant/approvalAdapters';
import type { WriteProposal, MultiActionConfig, EmailDraft, OfferDraft } from 'App/shadcn/pages/Assistant/types';
import workspaceTokens from 'App/shadcn/pages/AIWorkspace/styles/tokens';
import { chatThread } from 'App/shadcn/pages/AIWorkspace/Chat/styles/thread';
import { traceDrawer } from 'App/shadcn/pages/AIWorkspace/styles/traceDrawer';
import { legacyOverlayCloseBar } from 'App/shadcn/pages/AIWorkspace/styles/legacyOverlay';
import praxisTokens from 'App/shadcn/pages/Assistant/styles/tokens';
import { praxisActionCards } from 'App/shadcn/pages/Assistant/styles/actionCards';
import { praxisEmailDraft } from 'App/shadcn/pages/Assistant/styles/emailDraft';
import { praxisCardFields } from 'App/shadcn/pages/Assistant/styles/cardFields';

interface Props {
	clientId: number;
}

type SuggestionItem =
	| { key: number; kind: 'write'; proposal: WriteProposal }
	| { key: number; kind: 'multi'; config: MultiActionConfig }
	| { key: number; kind: 'email'; draft: EmailDraft }
	| { key: number; kind: 'offer'; draft: OfferDraft };

// crmMutation is emitted under a few historical action names; all render as WriteCard.
const CRM_ACTIONS = new Set(['crmMutation', 'proposeCrmChange', 'writeCRM']);

// Once an approval reaches one of these it has been dealt with — drop it so the
// section empties as suggestions are resolved. `error` / `failed` are kept on
// purpose so a failed execution stays visible (and retryable) instead of silently
// vanishing.
const RESOLVED_STATUSES = new Set(['saved', 'sent', 'cancelled', 'completed']);

const toItem = (a: AgentApproval): SuggestionItem | null => {
	// Company-card only: stamp the approval's created date so every card shows a
	// date — including the types (email / offer / bulk / client update) that carry
	// no natural date. The workspace doesn't set this, so it's unaffected.
	const createdDate = typeof a.regDate === 'string' ? a.regDate.slice(0, 10) : undefined;
	if (CRM_ACTIONS.has(a.action))
		return { key: a.id, kind: 'write', proposal: { ...approvalToWriteProposal(a), createdDate } };
	if (a.action === 'multiAction')
		return { key: a.id, kind: 'multi', config: { ...approvalToMultiActionConfig(a), createdDate } };
	if (a.action === 'sendEmail')
		return { key: a.id, kind: 'email', draft: { ...approvalToEmailDraft(a), createdDate } };
	if (a.action === 'draftOffer')
		return { key: a.id, kind: 'offer', draft: { ...approvalToOfferDraft(a), createdDate } };
	// permissionUpgrade and any unknown action have no card — skip, same as the inbox.
	return null;
};

/**
 * "Suggested by your AI assistant" — the agent's pending suggestions for this
 * company, shown above the activity timeline (WEB-7767). Mirrors
 * DashboardPinnedComments: self-fetches keyed by clientId, renders the same
 * cards as the AI Workspace inbox, and returns null when there's nothing to
 * show. Per-user visibility is enforced server-side (agentApprovalRequest
 * canView), so a rep only sees suggestions meant for them.
 */
const DashboardAiSuggestions = ({ clientId }: Props) => {
	const [items, setItems] = useState<SuggestionItem[]>([]);
	const [bulkBusy, setBulkBusy] = useState(false);

	useEffect(() => {
		// Never fetch without a real clientId: the Angular react-driver can mount
		// this before AccountCtrl.account has loaded, so clientId is briefly
		// undefined. A clientId-less request falls back to findForUser (the whole
		// inbox queue), which would then render on every company card — so gate
		// the fetch on a valid id and stay empty until it arrives (the driver's
		// `watch` re-renders us with the real id once the account resolves).
		if (!clientId || clientId <= 0) {
			setItems([]);
			return;
		}
		let cancelled = false;
		const fetchSuggestions = async () => {
			try {
				const approvals = await AgentApprovalsResource.list({ clientId, status: 'pending', limit: 50 });
				if (cancelled) return;
				setItems(approvals.map(toItem).filter((i): i is SuggestionItem => i !== null));
			} catch (err) {
				// Don't blank the company card on a transient failure — keep the
				// section empty and surface the cause for diagnosis.
				console.error('[DashboardAiSuggestions] failed to load suggestions', err);
			}
		};
		fetchSuggestions();
		const unlistenClientMerge = EventBus.on('account.merged', fetchSuggestions);
		return () => {
			cancelled = true;
			unlistenClientMerge();
		};
	}, [clientId]);

	// Each card resolves itself through AgentApprovalsResource and reports the
	// new status via onUpdate. Merge edits back into the item; drop it once
	// resolved so it disappears from the section.
	const onItemUpdate = (next: SuggestionItem, status: string | undefined) => {
		setItems(prev => {
			if (status && RESOLVED_STATUSES.has(status)) {
				return prev.filter(it => it.key !== next.key);
			}
			return prev.map(it => (it.key === next.key ? next : it));
		});
	};

	// Bulk resolve every pending suggestion at once. skipContinuation stops each
	// resolution from spawning its own headless agent run (the agent picks the
	// outcomes up from history). Ones that fail stay so the user can retry them.
	const resolveAll = async (action: 'approve' | 'reject') => {
		if (!items.length || bulkBusy) return;
		setBulkBusy(true);
		const ids = items.map(it => it.key);
		const results = await Promise.allSettled(
			ids.map(id =>
				action === 'approve'
					? AgentApprovalsResource.approve(id, undefined, undefined, { skipContinuation: true })
					: AgentApprovalsResource.reject(id, 'cancelled', undefined, { skipContinuation: true })
			)
		);
		const failed = new Set(ids.filter((_, i) => results[i].status === 'rejected'));
		setItems(prev => prev.filter(it => failed.has(it.key)));
		setBulkBusy(false);
	};

	const renderCard = (item: SuggestionItem): React.ReactNode => {
		switch (item.kind) {
			case 'write':
				return (
					<WriteCard
						key={item.key}
						proposal={item.proposal}
						onUpdate={u => onItemUpdate({ ...item, proposal: { ...item.proposal, ...u } }, u.status)}
					/>
				);
			case 'multi':
				return (
					<MultiActionCard
						key={item.key}
						config={item.config}
						onUpdate={(_id, u) => onItemUpdate({ ...item, config: { ...item.config, ...u } }, u.status)}
					/>
				);
			case 'email':
				return (
					<EmailDraftCard
						key={item.key}
						draft={item.draft}
						onUpdate={u => onItemUpdate({ ...item, draft: { ...item.draft, ...u } }, u.status)}
					/>
				);
			case 'offer':
				return (
					<OfferDraftCard
						key={item.key}
						draft={item.draft}
						onUpdate={u => onItemUpdate({ ...item, draft: { ...item.draft, ...u } }, u.status)}
					/>
				);
		}
	};

	if (!items.length) {
		return null;
	}

	// The proposal cards are styled by the workspace's `ws-*`/`praxis` CSS (custom
	// classes + CSS vars), NOT tailwind — ShadcnScope only injects tailwind. So we
	// inject the same two stylesheets the AI Workspace does and wrap the cards in
	// the `.ai-ws`/`.praxis` scopes that define the `--ws-*` / `--pr-*` vars they
	// rely on. Everything stays inside the shadow root, so none of it leaks onto
	// the company card.
	// AIGeneratedContentCard gives us the AI-summary look for free: the bordered
	// Card wrapper, the "Upsales AI" chip, the section header, and a collapse/expand
	// slide toggle (collapsed = just the header row). The suggestion cards render as
	// its children, inside a ShadcnScope so their ws-*/praxis styling applies.
	return (
		<AIGeneratedContentCard title="aiSuggestions.title" isLoading={false}>
			<ShadcnScope className="DashboardAiSuggestions">
				{/* The proposal cards are styled by the workspace's ws-* and praxis CSS
				    (custom classes + vars), not tailwind — ShadcnScope only injects
				    tailwind. Compose only the fragments whose classes the card
				    components actually use (WEB-8005 class-usage mapping): prop cards
				    from traceDrawer/actionCards, email fields from emailDraft/
				    cardFields, and the generic .ws-btn/.ws-btn-primary buttons that
				    live in chatThread/legacyOverlayCloseBar. Fragment order = the
				    canonical composition order in workspaceStyles/praxisStyles. */}
				<style>{workspaceTokens + chatThread + traceDrawer + legacyOverlayCloseBar}</style>
				<style>{praxisTokens + praxisActionCards + praxisEmailDraft + praxisCardFields}</style>
				{/* Scope the sheets under .ai-ws/.praxis wrappers below; keep the
				    shadow host + wrapper transparent so the cards sit on the card. */}
				<style>
					{`:host { background: transparent; }
					  .ai-ws .ws-trace-proposal-list { background: transparent; border: none; border-radius: 0; overflow: visible; gap: 8px; }
					  .ai-ws .ws-prop-card { background: var(--ws-panel); border: 1px solid var(--ws-line); border-radius: 10px; }
					  .ai-ws .pr-email-card-wrapper { margin: 0; }
					  /* Collapsed cards: clamp the meta row to one line so long free-text previews (e.g. a post-meeting call script) don't wrap and make the card look auto-expanded. */
					  .ai-ws .ws-prop-card--compact .ws-prop-card-meta { display: block; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }`}
				</style>
				{/* .ai-ws carries the --ws-* vars but is also the full-screen workspace
				    layout (grid/height/overflow) — neutralise it to a plain block. */}
				<div
					className="ai-ws praxis"
					style={{
						display: 'block',
						width: 'auto',
						height: 'auto',
						overflow: 'visible',
						padding: 0,
						background: 'transparent'
					}}
				>
					<div
						className="ws-prop-bulk-actions"
						style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '10px' }}
					>
						<button
							type="button"
							className="ws-btn ws-btn-secondary ws-prop-mini-btn"
							onClick={() => resolveAll('reject')}
							disabled={bulkBusy}
						>
							<span>{T('aiSuggestions.cancelAll')}</span>
						</button>
						<button
							type="button"
							className="ws-btn ws-btn-primary ws-prop-mini-btn"
							onClick={() => resolveAll('approve')}
							disabled={bulkBusy}
						>
							<span>{T('aiSuggestions.acceptAll')}</span>
						</button>
					</div>
					<div className="ws-trace-proposal-list">{items.map(renderCard)}</div>
				</div>
			</ShadcnScope>
		</AIGeneratedContentCard>
	);
};

export default DashboardAiSuggestions;
